import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Categories() {
    const { user } = useAuth();
    const canWrite = hasRole(user, "manager");
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", description: "", is_active: true });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/categories");
            setCategories(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm({ name: "", description: "", is_active: true }); setDialogOpen(true); };
    const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "", is_active: c.is_active }); setDialogOpen(true); };

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name required");
        try {
            if (editing) {
                await api.patch(`/categories/${editing.id}`, form);
                toast.success("Category updated");
            } else {
                await api.post("/categories", { name: form.name, description: form.description });
                toast.success("Category created");
            }
            setDialogOpen(false); load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    return (
        <div className="space-y-6" data-testid="categories-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Catalog</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Categories</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{categories.length} categor{categories.length === 1 ? "y" : "ies"} configured.</p>
                </div>
                {canWrite && (
                    <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="categories-add-btn">
                        <Plus size={16} weight="bold" className="mr-1.5" /> New Category
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-neutral-500">Loading...</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.map((c) => (
                        <div key={c.id} className="bg-white border border-neutral-200 rounded-md p-5 fade-up hover:border-neutral-400 transition-colors"
                             data-testid={`category-card-${c.name}`}>
                            <div className="flex items-start justify-between">
                                <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: "#DCFCE7" }}>
                                    <Tag size={20} weight="duotone" color="#15803D" />
                                </div>
                                {!c.is_active && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-neutral-900 text-white">Archived</span>
                                )}
                            </div>
                            <div className="mt-4">
                                <div className="text-lg font-bold font-display tracking-tight">{c.name}</div>
                                {c.description && <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{c.description}</div>}
                                <div className="text-xs text-neutral-500 mt-3">Products: <b className="text-neutral-900 tabular">{c.product_count || 0}</b></div>
                            </div>
                            {canWrite && (
                                <button onClick={() => openEdit(c)} className="mt-4 text-xs font-semibold text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1"
                                        data-testid={`category-edit-${c.name}`}>
                                    <Pencil size={13} /> Edit
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="category-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">{editing ? "Edit Category" : "New Category"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="category-form-name" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Description</Label>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} data-testid="category-form-description" />
                        </div>
                        {editing && (
                            <div className="flex items-center gap-3">
                                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="category-form-active" />
                                <Label>Active</Label>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="category-form-cancel">Cancel</Button>
                        <Button onClick={submit} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="category-form-submit">
                            {editing ? "Save Changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
